import type { Feature, FeatureCollection } from 'geojson';
import { GetModelPerformanceMapDataResponse } from 'types/Meteorology/Fields/ModelPerformance/getMapData';
import { ModelPerformanceMapData, ModelPerformanceLegendItem } from 'types/Meteorology/Fields/ModelPerformance/mapData';

/** Propriedades em snake_case que a API pode retornar (nome, cor, valor). */
interface RawFeatureProperties {
  nome?: string;
  name?: string;
  cor?: string;
  color?: string;
  valor?: number;
  value?: number;
  [key: string]: unknown;
}

/** Item de legenda em snake_case (API pode retornar assim). */
interface RawLegendItem {
  min?: number;
  max?: number | null;
  min_value?: number;
  max_value?: number | null;
  color?: string;
  cor?: string;
  label?: string;
  [key: string]: unknown;
}

/** Objeto de camadas por nome (ex.: { XINGU: FeatureCollection, ... }). */
type LayersObject = Record<string, FeatureCollection | Feature[] | undefined>;

/** Resposta bruta da API pode vir em snake_case e com chaves variadas. */
type RawData = {
  geo_json?: FeatureCollection | Feature[];
  geoJson?: FeatureCollection | Feature[];
  camadas?: FeatureCollection | Feature[] | LayersObject;
  layers?: FeatureCollection | Feature[] | LayersObject;
  geojson?: FeatureCollection | Feature[];
  legend?: RawLegendItem[] | ModelPerformanceLegendItem[];
  legenda?: RawLegendItem[] | ModelPerformanceLegendItem[];
  variable?: string;
  variavel?: string;
  unit?: string;
  unidade?: string;
  [key: string]: unknown;
};

/** Normaliza itens da legenda (min_value→min, cor→color, etc.). */
function normalizeLegend(
  raw: RawLegendItem[] | ModelPerformanceLegendItem[] | undefined,
): ModelPerformanceLegendItem[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((r) => {
    const item = r as RawLegendItem;
    return {
      min: item.min ?? item.min_value ?? 0,
      max: item.max ?? item.max_value ?? null,
      color: item.color ?? item.cor ?? '#3388ff',
      label: item.label ?? '',
    };
  });
}

/** Retorna a cor da legenda para um dado valor (faixa min/max que contém o valor). */
function getColorFromLegend(
  value: number,
  legend: ModelPerformanceLegendItem[],
): string {
  const item = legend.find(
    (r) => value >= r.min && (r.max === null || value <= r.max),
  );
  return item?.color ?? '#3388ff';
}

/** Garante FeatureCollection e normaliza properties (nome→name, cor→color, valor→value). */
function normalizeGeoJson(
  raw: FeatureCollection | Feature[] | undefined,
  legend?: ModelPerformanceLegendItem[],
): FeatureCollection | null {
  if (!raw) return null;

  const features: Feature[] = Array.isArray(raw)
    ? raw
    : (raw as FeatureCollection).features ?? [];

  if (features.length === 0) return null;

  const normalizedFeatures: Feature[] = features.map((f) => {
    if (f.type !== 'Feature' || !f.geometry) return f;
    const props = (f.properties ?? {}) as RawFeatureProperties;
    const value = props.value ?? props.valor;
    const colorFromApi = props.color ?? props.cor;
    const color = colorFromApi ?? (value != null && legend?.length
      ? getColorFromLegend(Number(value), legend)
      : '#3388ff');
    return {
      ...f,
      properties: {
        ...f.properties,
        name: props.name ?? props.nome,
        color: color as string,
        value,
      },
    };
  });

  return {
    type: 'FeatureCollection',
    features: normalizedFeatures,
  };
}

function transformGetMapDataResponse(
  response: GetModelPerformanceMapDataResponse & { data?: RawData },
): ModelPerformanceMapData {
  if (response.code !== 200) {
    throw new Error(response.message || 'Erro ao carregar dados do mapa');
  }

  const data = response.data as RawData | undefined;
  if (!data) {
    throw new Error('Resposta da API sem dados');
  }

  const dataMaybeGeo = data as unknown as FeatureCollection;
  const isFeatureCollection = dataMaybeGeo?.type === 'FeatureCollection';
  let rawGeoJson: FeatureCollection | Feature[] | LayersObject | undefined = data.geo_json
    ?? data.geoJson ?? data.camadas ?? data.layers ?? data.geojson
    ?? (isFeatureCollection ? dataMaybeGeo : undefined);

  if (rawGeoJson && typeof rawGeoJson === 'object' && !Array.isArray(rawGeoJson)
    && rawGeoJson.type !== 'FeatureCollection') {
    const layersObj = rawGeoJson as LayersObject;
    const allFeatures: Feature[] = [];
    Object.entries(layersObj).forEach(([layerName, fc]) => {
      if (!fc) return;
      const list = Array.isArray(fc) ? fc : (fc as FeatureCollection).features ?? [];
      list.forEach((f) => {
        allFeatures.push({
          ...f,
          properties: { ...(f.properties ?? {}), name: layerName },
        });
      });
    });
    rawGeoJson = allFeatures.length ? { type: 'FeatureCollection', features: allFeatures } : undefined;
  }

  const rawLegend = data.legend ?? data.legenda;
  const legend = normalizeLegend(rawLegend);
  const variable = data.variable ?? data.variavel ?? '';
  const unit = data.unit ?? data.unidade ?? '';

  if (!legend.length) {
    throw new Error('Resposta da API inválida: legend/legenda é obrigatório');
  }

  const geoJson = normalizeGeoJson(rawGeoJson as FeatureCollection | Feature[] | undefined, legend);

  if (!geoJson || !geoJson.features?.length) {
    throw new Error('Resposta da API inválida: geo_json/camadas com features são obrigatórios');
  }

  return {
    geoJson,
    legend,
    variable,
    unit,
  };
}

export default transformGetMapDataResponse;
